import { IonItem, IonLabel, IonRadio, IonRadioGroup } from "@ionic/react";
import { Chip, CircularProgress, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { useEffect, useState } from "react";

/** Campos compartidos por la solicitud y el cierre de Solicitudes de Capacitación. */

export const campoEstilo: React.CSSProperties = { marginBottom: 16, width: "100%" };

export const etiquetaEstilo: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 600,
  color: "#444",
  marginBottom: 6,
  display: "block",
};

export const Etiqueta: React.FC<{ texto: string; obligatorio?: boolean }> = ({ texto, obligatorio }) => (
  <span style={etiquetaEstilo}>
    {obligatorio ? "* " : ""}
    {texto}
  </span>
);

interface MultiSelectProps<T> {
  etiqueta: string;
  obligatorio?: boolean;
  opciones: T[];
  valor: T[];
  onChange: (v: T[]) => void;
  getId: (o: T) => number | string;
  getLabel: (o: T) => string;
  groupBy?: (o: T) => string;
  /** Texto extra por el que también se busca y que se muestra bajo la opción (ej. la categoría). */
  getDetalle?: (o: T) => string;
  ayuda?: string;
  disabled?: boolean;
  cargando?: boolean;
}

const normalizar = (texto: string) =>
  texto
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase();

/** Multiselect con buscador; los elegidos se listan debajo con opción de quitarlos. */
export function MultiSelect<T>(props: MultiSelectProps<T>) {
  const { etiqueta, obligatorio, opciones, valor, onChange, getId, getLabel, groupBy, getDetalle, ayuda, disabled, cargando } =
    props;
  // Sin acentos y por palabras sueltas: "maquina" encuentra "Uso y mantenimiento de máquinas".
  const filtrar = (lista: T[], { inputValue }: { inputValue: string }) => {
    const palabras = normalizar(inputValue).split(/\s+/).filter(Boolean);
    if (!palabras.length) return lista;
    return lista.filter((o) => {
      const texto = normalizar(`${getLabel(o)} ${getDetalle ? getDetalle(o) : ""}`);
      return palabras.every((p) => texto.includes(p));
    });
  };
  return (
    <div style={campoEstilo}>
      <Etiqueta texto={etiqueta} obligatorio={obligatorio} />
      <MuiAutocomplete
        multiple
        options={opciones}
        getOptionLabel={getLabel}
        getOptionSelected={(a, b) => getId(a) === getId(b)}
        filterOptions={filtrar}
        groupBy={groupBy}
        value={valor}
        onChange={(_, v) => onChange(v)}
        disableCloseOnSelect
        disabled={disabled}
        loading={cargando}
        loadingText="Cargando..."
        noOptionsText="Sin coincidencias"
        renderTags={(v) =>
          v.length === 0 ? null : (
            <Chip size="small" label={`${v.length} seleccionado${v.length === 1 ? "" : "s"}`} style={{ margin: 2 }} />
          )
        }
        renderOption={(o, { selected }) => (
          <span style={{ whiteSpace: "normal", lineHeight: 1.4 }}>
            {selected ? "✓ " : ""}
            {getLabel(o)}
            {getDetalle && getDetalle(o) ? (
              <small style={{ display: "block", color: "#888" }}>{getDetalle(o)}</small>
            ) : null}
          </span>
        )}
        renderInput={(params) => (
          <TextField
            {...params}
            variant="outlined"
            size="small"
            fullWidth
            placeholder={valor.length === 0 ? "Clic para seleccionar" : ""}
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {cargando ? <CircularProgress color="inherit" size={18} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {ayuda ? <small style={{ color: "#777" }}>{ayuda}</small> : null}
      {valor.length > 0 && (
        <div style={{ marginTop: 6, display: "flex", flexWrap: "wrap", gap: 4 }}>
          {valor.map((o) => (
            <Chip
              key={getId(o)}
              size="small"
              label={getLabel(o)}
              onDelete={disabled ? undefined : () => onChange(valor.filter((x) => getId(x) !== getId(o)))}
              style={{ maxWidth: "100%" }}
            />
          ))}
        </div>
      )}
    </div>
  );
}

interface RadioOpcionesProps {
  etiqueta: string;
  obligatorio?: boolean;
  opciones: { valor: string; etiqueta: string }[];
  valor: string | null;
  onChange: (v: string) => void;
  disabled?: boolean;
}

/** Grupo de radios en fila (se acomodan en varias líneas en pantallas chicas). */
export const RadioOpciones: React.FC<RadioOpcionesProps> = ({ etiqueta, obligatorio, opciones, valor, onChange, disabled }) => (
  <div style={campoEstilo}>
    <Etiqueta texto={etiqueta} obligatorio={obligatorio} />
    <IonRadioGroup value={valor ?? undefined} onIonChange={(e) => e.detail.value && onChange(e.detail.value)}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          border: "1px solid #ccc",
          borderRadius: 4,
          padding: "2px 4px",
        }}
      >
        {opciones.map((o) => (
          <IonItem key={o.valor} lines="none" style={{ flex: "1 1 140px", "--min-height": "36px" } as any}>
            <IonRadio slot="start" value={o.valor} disabled={disabled} style={{ marginRight: 8 }} />
            <IonLabel style={{ fontSize: 14 }}>{o.etiqueta}</IonLabel>
          </IonItem>
        ))}
      </div>
    </IonRadioGroup>
  </div>
);

export const SI_NO = [
  { valor: "SI", etiqueta: "Sí" },
  { valor: "NO", etiqueta: "No" },
];

export const ESTADOS_SOLICITUD: Record<string, { etiqueta: string; color: string }> = {
  PENDIENTE: { etiqueta: "Pendiente de coordinación", color: "#f0ad4e" },
  PROPUESTA: { etiqueta: "Nueva fecha propuesta", color: "#17a2b8" },
  PROGRAMADA: { etiqueta: "Programada", color: "#007bff" },
  PENDIENTE_CIERRE: { etiqueta: "Pendiente de cierre", color: "#6c757d" },
  CERRADA: { etiqueta: "Cerrada", color: "#28a745" },
  RECHAZADA: { etiqueta: "Rechazada", color: "#dc3545" },
  CANCELADA: { etiqueta: "Cancelada", color: "#343a40" },
};

export const EstadoChip: React.FC<{ estado: string }> = ({ estado }) => {
  const e = ESTADOS_SOLICITUD[estado] || { etiqueta: estado, color: "#999" };
  return (
    <span
      style={{
        background: e.color,
        color: "#fff",
        borderRadius: 10,
        padding: "2px 8px",
        fontSize: 11,
        whiteSpace: "nowrap",
      }}
    >
      {e.etiqueta}
    </span>
  );
};

/** "2026-09-30" -> "30/09/2026" */
export const fmtFecha = (f?: string | null) => {
  if (!f) return "";
  const [a, m, d] = f.substring(0, 10).split("-");
  return d && m && a ? `${d}/${m}/${a}` : f;
};

export const fmtFechaHora = (f?: string | null, h?: string | null) =>
  !f && !h ? "-" : `${f ? fmtFecha(f) : "sin fecha"}${h ? ` ${h}` : ""}`;

/** Fecha local YYYY-MM-DD desplazada n días desde hoy. */
export const isoHoyMas = (dias: number) => {
  const d = new Date();
  d.setDate(d.getDate() + dias);
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Autocomplete con búsqueda remota (debounce 300 ms). */
export function BuscadorRemoto<T>(props: {
  etiqueta: string;
  valor: T | null;
  onChange: (v: T | null) => void;
  buscar: (q: string) => Promise<T[]>;
  getLabel: (o: T) => string;
  getId: (o: T) => string | number;
  minimo?: number;
  disabled?: boolean;
  /** Vacía el buscador después de elegir (para ir sumando a una lista). */
  limpiarAlElegir?: boolean;
}) {
  const { etiqueta, valor, onChange, buscar, getLabel, getId, minimo = 2, disabled, limpiarAlElegir } = props;
  const [texto, setTexto] = useState("");
  const [opciones, setOpciones] = useState<T[]>([]);
  const [cargando, setCargando] = useState(false);

  useEffect(() => {
    if (texto.trim().length < minimo) {
      setOpciones(valor ? [valor] : []);
      return;
    }
    const t = window.setTimeout(() => {
      setCargando(true);
      buscar(texto.trim())
        .then(setOpciones)
        .catch(() => setOpciones([]))
        .finally(() => setCargando(false));
    }, 300);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [texto]);

  return (
    <MuiAutocomplete
      options={opciones}
      getOptionLabel={getLabel}
      getOptionSelected={(a, b) => getId(a) === getId(b)}
      value={valor}
      onChange={(_, v) => {
        onChange(v);
        if (limpiarAlElegir) {
          setTexto("");
          setOpciones([]);
        }
      }}
      inputValue={texto}
      onInputChange={(_, v, reason) => {
        if (reason !== "reset" || v !== "") setTexto(v);
      }}
      filterOptions={(o) => o}
      loading={cargando}
      loadingText="Buscando..."
      noOptionsText={texto.length < minimo ? `Escriba al menos ${minimo} caracteres` : "Sin coincidencias"}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          variant="outlined"
          placeholder={etiqueta}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {cargando ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

interface TablaPersonasProps {
  /** `etiqueta`: marca opcional junto al nombre (p. ej. "Agregado"). */
  personas: { legajo: string; nombre?: string | null; etiqueta?: string }[];
  /** Si se pasa, la tabla tiene una columna de casillas para marcar presentes. */
  seleccion?: {
    marcado: (legajo: string) => boolean;
    alternar: (legajo: string, valor: boolean) => void;
    disabled?: boolean;
  };
}

const celda: React.CSSProperties = { padding: "6px 8px", borderBottom: "1px solid #eee", fontSize: 14 };
const encabezado: React.CSSProperties = {
  ...celda,
  background: "#7a0c25",
  color: "#fff",
  fontWeight: 600,
  textAlign: "left",
  position: "sticky",
  top: 0,
};

/** Tabla de personas (una fila por operario/supervisor), con selección opcional. */
export const TablaPersonas: React.FC<TablaPersonasProps> = ({ personas, seleccion }) => (
  <div style={{ border: "1px solid #ddd", borderRadius: 4, overflow: "auto", maxHeight: 420 }}>
    <table style={{ width: "100%", borderCollapse: "collapse" }}>
      <thead>
        <tr>
          {seleccion && <th style={{ ...encabezado, width: 44, textAlign: "center" }}>✓</th>}
          <th style={{ ...encabezado, width: 36 }}>#</th>
          <th style={{ ...encabezado, width: 90 }}>Legajo</th>
          <th style={encabezado}>Nombre</th>
        </tr>
      </thead>
      <tbody>
        {personas.map((p, i) => {
          const activo = seleccion ? seleccion.marcado(p.legajo) : true;
          return (
            <tr
              key={p.legajo}
              onClick={
                seleccion && !seleccion.disabled ? () => seleccion.alternar(p.legajo, !activo) : undefined
              }
              style={{
                cursor: seleccion && !seleccion.disabled ? "pointer" : "default",
                background: i % 2 ? "#fafafa" : "#fff",
                color: activo ? "#222" : "#999",
              }}
            >
              {seleccion && (
                <td style={{ ...celda, textAlign: "center" }}>
                  <input
                    type="checkbox"
                    checked={activo}
                    disabled={seleccion.disabled}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => seleccion.alternar(p.legajo, e.target.checked)}
                    style={{ width: 18, height: 18 }}
                  />
                </td>
              )}
              <td style={celda}>{i + 1}</td>
              <td style={celda}>{p.legajo}</td>
              <td style={{ ...celda, textDecoration: activo ? "none" : "line-through" }}>
                {p.nombre}
                {p.etiqueta && (
                  <span
                    style={{
                      marginLeft: 6,
                      fontSize: 11,
                      background: "#fff3cd",
                      color: "#664d03",
                      borderRadius: 8,
                      padding: "1px 6px",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {p.etiqueta}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);
